import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card, Table, Form, Button, Badge, Alert, Modal } from 'react-bootstrap';
import { useGetServerHealthQuery, useGetEntraSigninsQuery, useGetNetworkEventsQuery } from '../slices/metricsApiSlice';
import Loader from '../components/Loader';

const InfrastructureStatusScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  // Protect route on frontend
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  // Hostname input state
  const [hostnameInput, setHostnameInput] = useState('');
  const [selectedHostname, setSelectedHostname] = useState('');

  // Modal and selected log details state
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  // Fetch hook (queries all stream events on load, or filters by host if selectedHostname is set)
  const { data, isLoading, error, refetch } = useGetServerHealthQuery(selectedHostname, {
    skip: !userInfo,
    pollingInterval: 180000,
    skipPollingIfUnfocused: true,
  });

  const { data: entraData, isLoading: isLoadingEntra, error: errorEntra, refetch: refetchEntra } = useGetEntraSigninsQuery(undefined, {
    skip: !userInfo,
    pollingInterval: 180000,
    skipPollingIfUnfocused: true,
  });

  const { data: networkData, isLoading: isLoadingNetwork, error: errorNetwork, refetch: refetchNetwork } = useGetNetworkEventsQuery(undefined, {
    skip: !userInfo,
    pollingInterval: 180000,
    skipPollingIfUnfocused: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSelectedHostname(hostnameInput.trim());
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Healthy':
        return <Badge bg="success" className="px-3 py-2 text-uppercase fs-6">Healthy</Badge>;
      case 'Warning':
        return <Badge bg="warning" className="px-3 py-2 text-dark text-uppercase fs-6">Warning</Badge>;
      case 'Critical':
        return <Badge bg="danger" className="px-3 py-2 text-uppercase fs-6">Critical</Badge>;
      default:
        return <Badge bg="secondary" className="px-3 py-2 text-uppercase fs-6">Unknown</Badge>;
    }
  };

  if (!userInfo) {
    return null;
  }

  return (
    <Container className="modern-directory-container py-4">
      {/* Inline styles for gorgeous premium dark look */}
      <style>{`
        .infra-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: #fff;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }
        .infra-card:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.2);
        }
        .infra-title {
          font-family: 'Outfit', 'Inter', sans-serif;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        .custom-input {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: #fff !important;
          border-radius: 8px;
        }
        .custom-input:focus {
          background: rgba(255, 255, 255, 0.12);
          border-color: #3b82f6;
          box-shadow: 0 0 0 0.25rem rgba(59, 130, 246, 0.25);
        }
        .custom-table,
        .custom-table th,
        .custom-table td {
          background-color: transparent !important;
          color: #e2e8f0;
        }
        .custom-table th {
          border-bottom: 2px solid rgba(255, 255, 255, 0.15) !important;
          color: #94a3b8;
          font-weight: 600;
        }
        .custom-table td {
          border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
          vertical-align: middle;
        }
        .custom-table tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.03) !important;
        }
        .alert-cleared {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid #ef4444;
          color: #fca5a5;
        }
        .custom-modal-content {
          background-color: #0f172a !important; /* Slate 900 background */
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          border-radius: 14px;
          color: #e2e8f0;
        }
      `}</style>

      {/* Header Block */}
      <div className="directory-header-block d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-end gap-3 mb-4">
        <div>
          <div className="directory-tag">Monitoring</div>
          <h1 className="directory-title infra-title">Infrastructure Status</h1>
          <p className="directory-subtitle">
            Query real-time event vectors and audit logs directly from our local Graylog server stream.
          </p>
        </div>
      </div>

      {/* Tools Section */}
      <Card className="infra-card p-4 mb-4">
        <h5 className="mb-3 fw-bold text-white">Infrastructure Tools</h5>
        <div className="d-flex gap-3">
          <Button variant="outline-primary" onClick={() => navigate('/network-scan')} className="px-4 py-2 fw-semibold">
            Network Scan Utility
          </Button>
          <Button variant="outline-primary" href="http://172.30.1.79:8501/" target="_blank" rel="noopener noreferrer" className="px-4 py-2 fw-semibold">
            Database Migration Tool
          </Button>
        </div>
        
      </Card>

      {/* Entra Signins View */}
      {entraData && !isLoadingEntra && !errorEntra && (
        <Card className="infra-card p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">Entra Sign-ins to Review</h5>
            <Badge bg="secondary">{entraData.signins?.length || 0} Events</Badge>
          </div>
          <div className="table-responsive" style={{ maxHeight: '350px' }}>
            <Table className="custom-table" variant="dark" hover size="sm">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Application</th>
                  <th>IP Address</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entraData.signins && entraData.signins.length > 0 ? (
                  entraData.signins.map((signin, index) => (
                    <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleLogClick(signin._fullLog)}>
                      <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {signin.createdDateTime ? new Date(signin.createdDateTime).toLocaleString() : 'N/A'}
                      </td>
                      <td className="small">{signin.userDisplayName}</td>
                      <td className="small text-truncate" style={{ maxWidth: '200px' }} title={signin.appDisplayName}>
                        {signin.appDisplayName}
                      </td>
                      <td className="small font-monospace">{signin.ipAddress}</td>
                      <td className="small">{signin.location}</td>
                      <td>
                        <Badge bg={signin.conditionalAccessStatus === 'success' ? 'success' : 'warning'}>
                          {signin.conditionalAccessStatus}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4 small">
                      No Entra sign-ins detected in search range.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* Network Events View */}
      {networkData && !isLoadingNetwork && !errorNetwork && (
        <Card className="infra-card p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">Network Events</h5>
            <Badge bg="secondary">{networkData.networkEvents?.length || 0} Events</Badge>
          </div>
          <div className="table-responsive" style={{ maxHeight: '350px' }}>
            <Table className="custom-table" variant="dark" hover size="sm">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Source IP</th>
                  <th>Hostname</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {networkData.networkEvents && networkData.networkEvents.length > 0 ? (
                  networkData.networkEvents.map((evt, index) => (
                    <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleLogClick(evt._fullLog)}>
                      <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {evt.timestamp ? new Date(evt.timestamp).toLocaleString() : 'N/A'}
                      </td>
                      <td className="small font-monospace">{evt.source}</td>
                      <td className="small">{evt.hostname}</td>
                      <td className="small text-truncate" style={{ maxWidth: '300px' }} title={evt.message}>
                        {evt.message}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4 small">
                      No network events detected in search range.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* Hostname Selection Form */}
      <Card className="infra-card p-4 mb-4">
        <Form onSubmit={handleSubmit} className="d-flex gap-3 align-items-end flex-wrap flex-sm-nowrap">
          <Form.Group className="flex-grow-1">
            <Form.Label className="text-muted small text-uppercase fw-bold">Filter by Hostname</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., AD-PRIMARY-01 (leave blank for all hosts)"
              value={hostnameInput}
              onChange={(e) => setHostnameInput(e.target.value)}
              className="custom-input"
            />
          </Form.Group>
          <Button type="submit" variant="primary" className="px-4 py-2" style={{ height: '46px', borderRadius: '8px' }}>
            Filter Host
          </Button>
          {(selectedHostname || hostnameInput) && (
            <Button 
              variant="outline-warning" 
              onClick={() => {
                setHostnameInput('');
                setSelectedHostname('');
              }} 
              className="py-2" 
              style={{ height: '46px', borderRadius: '8px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => { refetch(); refetchEntra(); refetchNetwork(); }} className="py-2" style={{ height: '46px', borderRadius: '8px', color: '#ccc', borderColor: 'rgba(255,255,255,0.2)' }}>
            Refresh
          </Button>
        </Form>
      </Card>

      {/* Loading state */}
      {(isLoading || isLoadingEntra || isLoadingNetwork) && (
        <div className="text-center py-5">
          <Loader />
          <h5 className="text-white mt-3">Fetching metric event vectors from Graylog...</h5>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <Alert variant="danger" className="border-0 shadow-lg text-white" style={{ background: 'rgba(239, 68, 68, 0.2)', borderLeft: '4px solid #ef4444' }}>
          <h5 className="alert-heading">Failed to Query Graylog API (Server Health)</h5>
          <p className="mb-0">{error?.data?.message || error?.data?.error || error.error || 'Server error occurred.'}</p>
        </Alert>
      )}

      {errorEntra && !isLoadingEntra && (
        <Alert variant="danger" className="border-0 shadow-lg text-white mt-3" style={{ background: 'rgba(239, 68, 68, 0.2)', borderLeft: '4px solid #ef4444' }}>
          <h5 className="alert-heading">Failed to Query Graylog API (Entra Signins)</h5>
          <p className="mb-0">{errorEntra?.data?.message || errorEntra?.data?.error || errorEntra.error || 'Server error occurred.'}</p>
        </Alert>
      )}

      {errorNetwork && !isLoadingNetwork && (
        <Alert variant="danger" className="border-0 shadow-lg text-white mt-3" style={{ background: 'rgba(239, 68, 68, 0.2)', borderLeft: '4px solid #ef4444' }}>
          <h5 className="alert-heading">Failed to Query Graylog API (Network Events)</h5>
          <p className="mb-0">{errorNetwork?.data?.message || errorNetwork?.data?.error || errorNetwork.error || 'Server error occurred.'}</p>
        </Alert>
      )}

      {/* Dashboard View */}
      {data && !isLoading && !error && (
        <>
          {/* Main Server Overview */}
          <Card className="infra-card p-4 mb-4">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="text-muted small text-uppercase fw-bold">Target Hostname</div>
                <h2 className="mb-1 fw-bold text-white">{data.hostname}</h2>
                <div className="text-muted small">
                  Last Checked: {new Date().toLocaleTimeString()} (Relative range: 12 hours)
                </div>
              </Col>
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <div className="text-muted small text-uppercase fw-bold mb-2">Overall Status</div>
                {getStatusBadge(data.generalStatus)}
              </Col>
            </Row>
          </Card>

          {/* Tamper Alert Warning */}
          {data.tamperAlert && (
            <Alert className="alert-cleared d-flex align-items-center gap-3 mb-4 shadow">
              <div className="fs-3">⚠️</div>
              <div>
                <h5 className="alert-heading mb-1 fw-bold">Logs Cleared Alert (Tamper Detected)</h5>
                <p className="mb-0 small">
                  EventID 1102 or 104 (Audit log clearing activity) was detected on host {data.hostname} in the last 12 hours. Check auditing logs immediately!
                </p>
              </div>
            </Alert>
          )}

          {/* Detailed Vectors */}
          <Row className="g-4">
            {/* Security Auditing Logs */}
            <Col xs={12} className="mb-4">
              <Card className="infra-card h-100 p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold">Security Auditing (Failed Logins & Audit)</h5>
                  <Badge bg="secondary">{data.securityLogs?.length || 0} Events</Badge>
                </div>
                <div className="table-responsive" style={{ maxHeight: '350px' }}>
                  <Table className="custom-table" variant="dark" hover size="sm">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>EventID</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.securityLogs && data.securityLogs.length > 0 ? (
                        data.securityLogs.map((log, index) => (
                          <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleLogClick(log)}>
                            <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                            </td>
                            <td>
                              <Badge bg="danger">{log.EventID || log.event_id || 'N/A'}</Badge>
                            </td>
                            <td className="small text-truncate" style={{ maxWidth: '250px' }} title={log.Category || 'No category'}>
                              {log.Category || 'N/A'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-4 small">
                            No recent security audit failures detected.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>

            {/* Software Updates (Recent Patches) */}
            <Col xs={12} className="mb-4">
              <Card className="infra-card h-100 p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold">Recent Patches & Software Updates</h5>
                  <Badge bg="secondary">{data.recentPatches?.length || 0} Events</Badge>
                </div>
                <div className="table-responsive" style={{ maxHeight: '350px' }}>
                  <Table className="custom-table" variant="dark" hover size="sm">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>EventID</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentPatches && data.recentPatches.length > 0 ? (
                        data.recentPatches.map((log, index) => (
                          <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleLogClick(log)}>
                            <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                            </td>
                            <td>
                              <Badge bg="info">{log.EventID || log.event_id || 'N/A'}</Badge>
                            </td>
                            <td className="small text-truncate" style={{ maxWidth: '250px' }} title={log.Category || 'No category'}>
                              {log.Category || 'N/A'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-4 small">
                            No recent patch or installation activity.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>

            {/* System Warnings (Level 3 Events) */}
            <Col xs={12}>
              <Card className="infra-card p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold">System Warnings (Level 3 Logging events)</h5>
                  <Badge bg="secondary">{data.systemWarnings?.length || 0} Events</Badge>
                </div>
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                  <Table className="custom-table" variant="dark" hover size="sm">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Level</th>
                        <th>EventID</th>
                        <th>Source</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.systemWarnings && data.systemWarnings.length > 0 ? (
                        data.systemWarnings.map((log, index) => (
                          <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleLogClick(log)}>
                            <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                            </td>
                            <td>
                              <Badge bg="warning" className="text-dark">Warn (3)</Badge>
                            </td>
                            <td>
                              <Badge bg="secondary">{log.EventID || log.event_id || 'N/A'}</Badge>
                            </td>
                            <td className="small font-monospace">{log.source || log.Source || 'Unknown'}</td>
                            <td className="small text-truncate" style={{ maxWidth: '400px' }} title={log.Category || 'No category'}>
                              {log.Category || 'N/A'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4 small">
                            No Level 3 System Warnings detected in search range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}



      {/* Log Detail Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        size="lg" 
        centered 
        contentClassName="custom-modal-content"
      >
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title className="fw-bold text-white">Log Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedLog ? (
            <div>
              <div className="mb-3">
                <span className="text-muted small text-uppercase d-block mb-1">Timestamp</span>
                <span className="fw-semibold text-white">
                  {selectedLog.timestamp ? new Date(selectedLog.timestamp).toLocaleString() : 'N/A'}
                </span>
              </div>
              <Row className="mb-3">
                <Col sm={6}>
                  <span className="text-muted small text-uppercase d-block mb-1">Source Host</span>
                  <span className="font-monospace text-info">{selectedLog.source || 'N/A'}</span>
                </Col>
                <Col sm={6}>
                  <span className="text-muted small text-uppercase d-block mb-1">Event ID / Code</span>
                  <span>
                    <Badge bg="danger" className="fs-6">
                      {selectedLog.EventID || selectedLog.event_id || 'N/A'}
                    </Badge>
                  </span>
                </Col>
              </Row>
              <div className="mb-3">
                <span className="text-muted small text-uppercase d-block mb-1">Full Message</span>
                <pre 
                  className="p-3 rounded text-light bg-black border border-secondary" 
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '250px', overflowY: 'auto', fontFamily: 'monospace' }}
                >
                  {selectedLog.full_message || selectedLog.message || 'No description'}
                </pre>
              </div>
              <div>
                <span className="text-muted small text-uppercase d-block mb-2">All Indexed Fields</span>
                <pre 
                  className="p-3 rounded text-info bg-black border border-secondary small" 
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '250px', overflowY: 'auto', fontFamily: 'monospace' }}
                >
                  {JSON.stringify(selectedLog, null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-muted">No log details selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default InfrastructureStatusScreen;
